/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import imgix from 'js/lib/imgix';

import { Button, Grid, PageGridContainer, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/front-page';

const imgParams = {
  quality: 80,
};

const styles = {
  container: () => css`
    ${breakpoints.up('md')} {
      margin-bottom: -16px;
    }
  `,
  // Setting z-index to keep header text on top of header image
  headerContainer: () => css`
    padding: 24px 0;
    position: relative;
    text-align: center;
    ${breakpoints.up('md')} {
      padding: 92px 24px 92px 0;
      text-align: left;
    }
  `,
  // Overrides d2 font-size temporarily.
  // TODO: Should be removed after upgrading to the next CDS version.
  header: () => css`
    && {
      font-size: 24px;
      ${breakpoints.up('sm')} {
        font-size: 32px;
      }
      ${breakpoints.up('md')} {
        font-size: 40px;
      }
    }
  `,
  description: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
    ${breakpoints.up('sm')} {
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  actionContainer: () => css`
    display: flex;
    gap: 16px;
    flex-direction: column;
    ${breakpoints.up('sm')} {
      flex-direction: row;
    }

    ${breakpoints.down('sm')} {
      justify-content: center;
    }
  `,
  primaryButton: () => css`
    border: 2px solid var(--cds-color-blue-700);
    padding: 6px 48px;
    font-weight: 600;
    border-radius: 8px;
    max-width: unset;

    :focus {
      border: 2px solid var(--cds-color-blue-100);
    }
  `,
  secondaryButton: () => css`
    padding-top: 6px;
    padding-bottom: 6px;
    border: 2px solid var(--cds-color-grey-300);
    color: var(--cds-color-grey-975);
    font-weight: 600;
    border-radius: 8px;
    box-shadow: unset;
    max-width: unset;

    :hover {
      box-shadow: unset;
      border-color: var(--cds-color-blue-700);
    }

    :focus {
      box-shadow: unset;
    }
  `,
  imgContainer: (_: Theme, backgroundImage?: string | null) => css`
    height: 100%;
    width: 100%;
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

type PropsFromCaller = {
  content?: Maybe<MarketingContentFieldsFragment>;
  secondaryButtonLabel?: string | null;
  secondaryButtonLink?: string | null;
};

// Description and CTA are Required.
const isContentfulContentValid = (
  content?: Maybe<MarketingContentFieldsFragment>
): content is Required<MarketingContentFieldsFragment> =>
  !!(content && content.description && content.callToActionLabel);

const HeroBannerRedesignCAFirst = ({ content, secondaryButtonLabel, secondaryButtonLink }: PropsFromCaller) => {
  const theme = useTheme();
  const { description, callToActionLabel, callToActionLink, visual, title } = content || {};

  if (!isContentfulContentValid(content)) {
    return null;
  }

  const heroImageSrc = visual?.url ? imgix.getOptimizedSrcs(visual?.url, 702, 483, imgParams).dpr2 : undefined;

  return (
    <TrackedDiv
      trackingName="hero_module_gwgcyber"
      withVisibilityTracking={true}
      requireFullyVisible={false}
      trackClicks={false}
    >
      <section data-testid="front-page-banner">
        <PageGridContainer>
          <Grid item container css={styles.container}>
            <Grid item md={6} lg={5} xs={12}>
              <div css={styles.headerContainer}>
                <SeoPageHeader />
                <Typography variant="d2semibold" component="h2" css={styles.header}>
                  {title}
                </Typography>
                <Typography2 variant="bodyPrimary" component="p" color="supportText" css={styles.description}>
                  {description}
                </Typography2>
                <div css={styles.actionContainer}>
                  <Button
                    component={TrackedLink2}
                    requireFullyVisible={false}
                    withVisibilityTracking={false}
                    trackingName="hero_module_gwgcyber_getstarted"
                    css={styles.primaryButton}
                    data-elementtiming="frontpage.banner.cta"
                    href={callToActionLink}
                  >
                    {callToActionLabel}
                  </Button>
                  <Button
                    component={TrackedLink2}
                    trackingName="hero_module_gwgcyber_explore"
                    withVisibilityTracking={false}
                    requireFullyVisible={false}
                    css={styles.secondaryButton}
                    variant="secondary"
                    href={secondaryButtonLink}
                    role="link"
                  >
                    {secondaryButtonLabel}
                  </Button>
                </div>
              </div>
            </Grid>
            <Grid item md={6} lg={7}>
              <div css={styles.imgContainer(theme, heroImageSrc)} />
            </Grid>
          </Grid>
        </PageGridContainer>
      </section>
    </TrackedDiv>
  );
};

export default HeroBannerRedesignCAFirst;
